// Warm the post-login route chunks while the login page is idle, so the first
// navigation after sign-in renders from the module cache. Rationale, role
// targeting (`user` → `/v2`; platform roles → `/admin`), and boot-path history:
// docs/solutions/performance-issues/prefetch-app-shell-during-login.md

type ChunkLoader = () => Promise<unknown>;

// Invariant: these specifiers MUST resolve to the same modules as App.tsx's
// route-level `React.lazy` factories (the `@/` alias and App.tsx's `./` path both
// land on `src/pages/v2/…`), so Rollup dedupes to one emitted chunk — a drifted
// path silently emits a duplicate. Keep the list a tight 2 (landing + top
// destination); see the solution doc above for the full rationale.
export const APP_SHELL_LOADERS: readonly ChunkLoader[] = [
  async () => await import('@/pages/v2/v2-dashboard-page'),
  async () => await import('@/pages/v2/v2-correspondence-page'),
];

interface NetworkInformation {
  readonly saveData?: boolean;
  readonly effectiveType?: string;
}

// Respect an explicit data-saver signal or a genuinely slow link — speculative
// prefetch is a latency hedge, not worth the bytes when the user has asked to
// conserve them or the connection can't spare the throughput.
function isConstrainedConnection(): boolean {
  const connection = (globalThis.navigator as Navigator & { connection?: NetworkInformation })
    ?.connection;
  if (!connection) {
    return false;
  }
  return (
    connection.saveData === true ||
    connection.effectiveType === '2g' ||
    connection.effectiveType === 'slow-2g'
  );
}

export interface IdleScheduler {
  readonly request: (callback: () => void) => number;
  readonly cancel: (handle: number) => void;
}

// `requestIdleCallback` keeps the prefetch off the main thread's critical work;
// the `setTimeout` fallback covers Safari and jsdom, which lack it. The 2s
// timeout caps how long the browser may defer the first load on a busy tab.
export function getIdleScheduler(): IdleScheduler {
  if (
    typeof globalThis.requestIdleCallback === 'function' &&
    typeof globalThis.cancelIdleCallback === 'function'
  ) {
    // Invoke through `globalThis` rather than detaching the reference — these are
    // Window IDL methods, and calling them on their receiver is the canonical
    // form (and mirrors the setTimeout branch below).
    return {
      request: (callback) => globalThis.requestIdleCallback(callback, { timeout: 2000 }),
      cancel: (handle) => {
        globalThis.cancelIdleCallback(handle);
      },
    };
  }
  return {
    request: (callback) => globalThis.setTimeout(callback, 200) as unknown as number,
    cancel: (handle) => {
      globalThis.clearTimeout(handle);
    },
  };
}

const NOOP = (): void => {
  /* nothing scheduled */
};

/**
 * Speculatively load the post-login route chunks during the login page's idle
 * time. Best-effort and side-effect-only — the return value is discarded; a
 * miss simply falls back to the on-demand load after navigation.
 *
 * Loaders run **staggered**, one per idle period, not concurrently: the started
 * import cannot be cancelled, so serialising keeps the speculative download from
 * contending with the auth request or first paint. The returned disposer cancels
 * any not-yet-fired idle callback (call it from an effect cleanup), so unmount —
 * or a submit that flips the caller's `enabled` gate — stops the chain before it
 * starts the next chunk.
 *
 * @param loaders - chunk loaders to warm, in priority order (injectable for tests).
 * @param scheduler - idle scheduler (injectable for tests).
 * @returns a disposer that cancels the pending stage.
 */
export function prefetchAppShell(
  loaders: readonly ChunkLoader[] = APP_SHELL_LOADERS,
  scheduler: IdleScheduler = getIdleScheduler(),
): () => void {
  if (isConstrainedConnection() || loaders.length === 0) {
    return NOOP;
  }

  let cancelled = false;
  let handle: number | undefined;

  const scheduleFrom = (index: number): void => {
    if (cancelled || index >= loaders.length) {
      return;
    }
    handle = scheduler.request(() => {
      if (cancelled) {
        return;
      }
      const scheduleNext = (): void => {
        if (!cancelled) {
          scheduleFrom(index + 1);
        }
      };
      // Advance on fulfilment, rejection, OR a synchronous throw from the loader:
      // the routes are independent, so one failed warm (a transient chunk-fetch
      // error) must never strand the rest — the miss is re-tried on demand when
      // that route mounts. Deferring the call through a resolved promise turns a
      // synchronous throw (a non-async or misbehaving injected loader) into a
      // rejection the `onRejected` handler absorbs, keeping the best-effort
      // guarantee total across the injectable loader seam; production loaders are
      // `async () => import(...)`. The terminal `.catch` is only the
      // no-floating-promises handler and stays silent — nothing reaches it in
      // practice (`scheduleNext` just checks `cancelled` and calls the
      // receiver-safe scheduler), so it logs no non-actionable noise (LOGGING.md).
      Promise.resolve()
        .then(async () => await loaders[index]())
        .then(scheduleNext, scheduleNext)
        .catch(() => {
          /* best-effort: no error path reaches here (see above) */
        });
    });
  };

  scheduleFrom(0);

  return () => {
    cancelled = true;
    if (handle !== undefined) {
      scheduler.cancel(handle);
    }
  };
}
